import {
    IonButton,
    IonContent,
    IonFooter,
    IonIcon,
    IonLabel,
    IonLoading,
    IonPage,
    IonText,
    useIonRouter,
} from "@ionic/react";
import { AxiosError } from "axios";
import React, { useEffect, useState } from "react";
import { FieldValues, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { AlertComponent, HeaderComponent } from "../../components";
import { useSafeKeyboard } from "../../components/hooks/useSafeKeyboard";
import { setDeveloperToolsEnabled } from "../../redux/actions/developerToolsAction";
import { LoginSession } from "../../redux/models/loginSession";
import { RootState, getLastLoggedInEmail } from "../../redux/store";
import {
    checkForEmail,
    checkForUsername,
    login,
    requestLoginCode,
} from "../../services/authService";
import { completeLogin } from "../../services/loginService";
import { mpEnable } from "../../services/analytics/mixpanelService";
import { trackRegistration } from "../../services/analytics/analyticsService";
import { useCurrentInAppVersion } from "../../components/hooks/appVersionHooks";
import { routes } from "../../route/constants";
import { bug } from "ionicons/icons";
import { useLocation } from "react-router";

import {
    LoginCodeEntry,
    LoginEmailForm,
    PasswordLoginForm,
} from "./components";

import "./login.css";
import "../../css/general.css";
import "../../css/forms.css";

export default function Login() {
    const route = useIonRouter();
    const location = useLocation<{ redirectTo?: string }>();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const inAppVersion = useCurrentInAppVersion();
    const { isOpen: isKeyboardVisible } = useSafeKeyboard();
    const formMethods = useForm<FieldValues>({
        mode: "onSubmit",
        reValidateMode: "onChange",
    });
    const {
        handleSubmit,
        formState: { errors },
        reset,
        trigger,
        getValues,
        watch,
    } = formMethods;

    const [showLoading, setShowLoading] = useState<boolean>(false);
    const [showAlert, setShowAlert] = useState<boolean>(false);
    const [alertDesc, setAlertDesc] = useState<string>("");
    const [alertTitle, setAlertTitle] = useState<string>("");
    const [loginAlertTestId, setLoginAlertTestId] =
        useState<string>("login-alert");
    const [codeSentToEmail, setCodeSentToEmail] = useState<string | null>(null);
    const [isPasswordMode, setIsPasswordMode] = useState<boolean>(false);
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [versionClickCount, setVersionClickCount] = useState<number>(0);

    const developerToolsEnabled = useSelector(
        (state: RootState) => state.developerTools.enabled
    );
    const userStorageEmail = useSelector(
        (state: RootState) => state.user.usernameOrEmail
    );

    const emailValue = (watch("email") as string) || "";
    const isEmailEntered = !!emailValue.trim();
    const showCodeEntry = !!codeSentToEmail;

    const loginTitleTranslation = t("login.login.title");
    const onboardingHeading = t("help.OnboardingStepThree.heading");
    const heroTitle =
        onboardingHeading === "help.OnboardingStepThree.heading"
            ? loginTitleTranslation
            : onboardingHeading;

    const sendLoginCodeTranslation = t("login.login.sendLoginCode");
    const sharedContinueTranslation = t("settings.WatchSettings.continue");
    const continueLabel =
        sharedContinueTranslation === "settings.WatchSettings.continue"
            ? sendLoginCodeTranslation
            : sharedContinueTranslation;

    useEffect(() => {
        // Use Redux state first, fallback to localStorage (preserved after logout)
        const emailToUse = userStorageEmail || getLastLoggedInEmail();
        if (emailToUse) {
            reset({
                email: emailToUse.replace(/\s/g, ""),
            });
        }
    }, [userStorageEmail, reset]);

    const developerAccess = () => {
        if (versionClickCount === 4) {
            dispatch(setDeveloperToolsEnabled(true));
        } else if (versionClickCount < 4) {
            setVersionClickCount(versionClickCount + 1);
        }
    };

    function showGeneralError() {
        setLoginAlertTestId("login-alert");
        setAlertTitle("");
        setAlertDesc(t("general.errorTryAgain"));
        setShowAlert(true);
    }

    // Callers own the progress indicator: password login has already raised the page-level
    // IonLoading, and code login shows its own verifying state. Raising the overlay here raced
    // OnBoardingManager's redirect after a code login and left it orphaned over onboarding.
    async function loginResponse(response: any, username: string) {
        if (response.status === 200) {
            const session = response.data as LoginSession;
            const status = session.status;
            const isNewUser = status === "new";

            await completeLogin({
                session,
                email: username,
                isNewUser,
            });

            if (isNewUser) {
                mpEnable();
                const provider = session.provider || "local";
                trackRegistration(provider);
                setShowLoading(false);
                route.push(routes.registerStepTwo, "forward", "replace");
                return;
            }

            const redirectTo =
                typeof location.state?.redirectTo === "string"
                    ? location.state.redirectTo
                    : undefined;
            if (redirectTo) {
                route.push(redirectTo, "forward", "replace");
            }
        } else {
            showGeneralError();
        }
        setShowLoading(false);
    }

    const logUserIn = async (usernameOrEmail: string, password: string) => {
        try {
            const response = await login(usernameOrEmail, password);
            await loginResponse(response, usernameOrEmail);
        } catch (error) {
            setShowLoading(false);
            const axError = error as AxiosError;
            if (axError.response && axError.response.status === 401) {
                setLoginAlertTestId("login-alert");
                setAlertTitle(t("login.login.failure.title"));
                setAlertDesc(t("login.login.failure.description"));
                setShowAlert(true);
            } else {
                showGeneralError();
            }
        }
    };

    async function userClickedLogin(data: any) {
        setShowLoading(true);
        const emailOrUsername = data.email;

        if (emailOrUsername && emailOrUsername.includes("@")) {
            const email = emailOrUsername.toLowerCase().trim();
            try {
                const response = await checkForEmail(email);
                if (response && response.status === 200) {
                    setLoginAlertTestId("login-alert-email-not-exist");
                    setAlertTitle(t("register.register.customerFailure.title"));
                    setAlertDesc(t("login.login.emailDoesNotExist"));
                    setShowLoading(false);
                    setShowAlert(true);
                    return;
                }
            } catch (e: any) {
                if (e.response && e.response.status === 409) {
                    await logUserIn(email, data.password);
                    return;
                }
                showGeneralError();
                setShowLoading(false);
                return;
            }
        } else {
            const username = emailOrUsername?.toLowerCase().trim();
            try {
                await checkForUsername(username);
                setLoginAlertTestId("login-alert-username-not-exist");
                setAlertTitle(t("register.register.customerFailure.title"));
                setAlertDesc(t("login.login.usernameDoesNotExist"));
                setShowLoading(false);
                setShowAlert(true);
                return;
            } catch (e: any) {
                if (e.response && e.response.status === 409) {
                    await logUserIn(username, data.password);
                    return;
                }
                showGeneralError();
                setShowLoading(false);
                return;
            }
        }
    }

    const handleCodeRequest = async () => {
        setCodeSentToEmail(null);
        const isValid = await trigger("email");
        if (!isValid) {
            return;
        }
        const email = (getValues("email") as string)?.toLowerCase().trim();
        if (!email) {
            return;
        }

        setShowLoading(true);
        try {
            await requestLoginCode(email);
            setCodeSentToEmail(email);
        } catch (error) {
            const errorCode = (error as AxiosError<any>).response?.data?.error;
            setLoginAlertTestId("login-alert-login-code-error");
            setAlertTitle(t("login.login.loginCodeErrorTitle"));
            setAlertDesc(
                errorCode === "no_account"
                    ? t("login.login.emailDoesNotExist")
                    : errorCode
                    ? t(`login.login.loginCode.errors.${errorCode}`)
                    : t("general.errorTryAgain")
            );
            setShowAlert(true);
        } finally {
            setShowLoading(false);
        }
    };

    const togglePasswordMode = () => {
        setCodeSentToEmail(null);
        setIsPasswordMode((prev) => !prev);
    };

    return (
        <IonPage className="login-page scorza-login">
            <HeaderComponent title={heroTitle} />
            <IonContent data-testid="login-page-header" fullscreen={true}>
                <h2
                    className="onboarding-section-header"
                    style={{ marginBottom: 60 }}
                >
                    {t("login.login.subtitle")}
                </h2>
                <FormProvider {...formMethods}>
                    {codeSentToEmail ? (
                        <LoginCodeEntry
                            email={codeSentToEmail}
                            onVerified={(response) =>
                                loginResponse(response, codeSentToEmail)
                            }
                        />
                    ) : isPasswordMode ? (
                        <PasswordLoginForm
                            onSubmit={userClickedLogin}
                            showPassword={showPassword}
                            onTogglePasswordVisibility={() =>
                                setShowPassword(!showPassword)
                            }
                        />
                    ) : (
                        <LoginEmailForm
                            onSubmit={handleCodeRequest}
                            continueLabel={continueLabel}
                            isEmailEntered={isEmailEntered}
                        />
                    )}
                </FormProvider>

                <IonLoading isOpen={showLoading} data-testid="login" />
                <AlertComponent
                    testId={loginAlertTestId}
                    showAlert={showAlert}
                    buttonDone={t("help.OnboardingVideo.controls.ok")}
                    dismissAlert={() => setShowAlert(false)}
                    description={alertDesc}
                    title={alertTitle}
                    onDonePressed={() => {}}
                    buttonAltText={""}
                />
            </IonContent>

            <IonFooter>
                {!isKeyboardVisible && (
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "16px",
                            alignItems: "center",
                            marginTop: "auto",
                            padding: "24px",
                        }}
                    >
                        <IonText
                            onClick={developerAccess}
                            style={{
                                fontSize: "0.75rem",
                                letterSpacing: "0.08em",
                                textTransform: "uppercase",
                                opacity: 0.5,
                            }}
                        >
                            {t("menu.version", {
                                versionNumber: inAppVersion || "DEV",
                            })}
                        </IonText>
                        {developerToolsEnabled && (
                            <IonButton
                                routerLink={routes.developerTools}
                                style={{ borderRadius: "12px" }}
                            >
                                <IonIcon icon={bug} slot="start" />
                                <IonLabel>Developer Tools</IonLabel>
                            </IonButton>
                        )}
                    </div>
                )}

                {showCodeEntry ? (
                    <IonButton
                        fill="clear"
                        expand="block"
                        onClick={togglePasswordMode}
                        className="uppercase"
                        type="button"
                        data-testid="toggle-password-mode"
                        style={{ color: "white" }}
                    >
                        {t("login.login.usePassword")}
                    </IonButton>
                ) : isPasswordMode ? (
                    <>
                        <IonButton
                            fill="clear"
                            expand="block"
                            data-testid="toggle-password-mode"
                            onClick={togglePasswordMode}
                            type="button"
                        >
                            {t("login.login.useLoginCode")}
                        </IonButton>
                        <IonButton
                            data-testid="loginButton"
                            className="uppercase"
                            type="submit"
                            expand="block"
                            onClick={handleSubmit(userClickedLogin)}
                        >
                            {t("general.login")}
                        </IonButton>
                    </>
                ) : (
                    <>
                        <IonButton
                            fill="clear"
                            expand="block"
                            onClick={togglePasswordMode}
                            type="button"
                            data-testid="toggle-password-mode"
                        >
                            {t("login.login.usePassword")}
                        </IonButton>
                        <IonButton
                            expand="block"
                            onClick={handleCodeRequest}
                            className="uppercase"
                            type="button"
                            data-testid="send-login-code"
                            disabled={!isEmailEntered}
                        >
                            {continueLabel}
                        </IonButton>
                    </>
                )}
            </IonFooter>
        </IonPage>
    );
}
